import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { hotKeywords } from "@/mocks/movies";
import { searchMovies } from "@/utils/movie";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => searchMovies(query), [query]);

  const submit = (value: string) => {
    const next = value.trim();
    const params = new URLSearchParams();
    if (next) params.set("q", next);
    setSearchParams(params);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(keyword);
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div className="mx-auto max-w-page px-4 md:px-8 pt-8 md:pt-12">
      <Breadcrumb items={[{ label: "搜索影视" }]} />

      <h1 className="mt-6 font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
        搜索午夜影视资源
      </h1>
      <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
        输入影视名称、类型、地区或导演关键词，即可快速匹配站内的午夜影视内容。
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col sm:flex-row gap-3 rounded-lg bg-background-100 border border-background-300/60 p-3 md:p-4"
      >
        <div className="flex-1 flex items-center gap-3 h-12 px-4 rounded-md bg-background-50 border border-background-300/70 focus-within:border-primary-500/70 transition-colors">
          <i className="ri-search-line text-lg text-foreground-500"></i>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="例如：午夜列车、悬疑、欧美"
            className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
          />
          {keyword && (
            <button
              type="button"
              aria-label="清空"
              onClick={() => setKeyword("")}
              className="w-6 h-6 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-950 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-search-2-line text-lg"></i>
          搜索
        </button>
      </form>

      {hasQuery ? (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <span>关键词：</span>
            <span className="px-3 py-1 rounded-md bg-accent-500/20 text-accent-200 font-medium">{query}</span>
            <span>共找到</span>
            <span className="text-primary-400 font-semibold">{results.length}</span>
            <span>条结果</span>
          </div>

          {results.length === 0 ? (
            <div className="mt-6 rounded-lg bg-background-100 border border-background-300/60 py-16 px-6 text-center">
              <span className="mx-auto w-16 h-16 rounded-full bg-background-200/80 flex items-center justify-center text-foreground-600">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="mt-5 font-heading font-bold text-lg text-foreground-950">没有找到相关影视</h2>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或者从下面的热门搜索里挑一个感兴趣的作品。
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {hotKeywords.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => submit(word)}
                    className="px-3.5 py-2 rounded-md bg-background-200/70 text-foreground-700 text-sm hover:bg-primary-500/15 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-fire-line mr-1 text-primary-400"></i>
                    {word}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {results.map((movie, index) => (
                <MovieCard key={movie.id} movie={movie} index={index} />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 rounded-lg bg-background-100 border border-background-300/60 p-6 md:p-8">
          <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
            <i className="ri-fire-line text-primary-400"></i>
            热门搜索
          </h2>
          <p className="mt-2 text-sm text-foreground-600">还没有输入关键词，先从大家都在搜的内容里找找灵感。</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => submit(word)}
                className="px-3.5 py-2 rounded-md bg-background-200/70 text-foreground-700 text-sm hover:bg-primary-500/15 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
          >
            <i className="ri-compass-3-line"></i>
            或者直接浏览影视分类
          </Link>
        </div>
      )}
    </div>
  );
}